import {
  UserPresenceDisplayStatus,
  getUserPresenceLabel,
} from '../../../../../store/user/user.models';
import type { ChannelMeetingStatus, MeetingCallMode } from '../../../../../store/meeting/meeting.models';

import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { ChatComposerMessage, ChatMention, ChatReplyPreview, ChatTaskReference, MessageItem, getAvatarAccent } from '../../home.models';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import 'emoji-picker-element';
import { UserHoverCardComponent } from '../user-hover-card/user-hover-card.component';

type ChatEditPayload = {
  messageId: string;
  text: string;
};

type ChatReactionPayload = {
  messageId: string;
  emoji: string;
};

type MessageReaction = {
  emoji: string;
  count: number;
  reactedByCurrentUser: boolean;
  userIds: string[];
};

type ReactionParticipant = {
  id: string;
  name: string;
  initials: string;
  accent: string;
};

type ComposerTokenMatch = {
  query: string;
  start: number;
  end: number;
};

type MessageTextSegment = {
  text: string;
  mention: boolean;
  taskReference: boolean;
  mentionedCurrentUser: boolean;
  mentionedUserId?: string | null;
  mentionedUserName?: string | null;
  taskReferenceData?: ChatTaskReference | null;
};

type PendingComposerAttachment = {
  id: string;
  file: File;
  previewUrl: string | null;
};

type GroupedAttachmentState = {
  byLeadId: Map<string, MessageItem[]>;
  followerIds: Set<string>;
};

const DEFAULT_REACTION_EMOJIS = ['👍', '❤️', '🤪', '😮'] as const;
const MAX_PENDING_ATTACHMENTS = 5;
const REACTION_PICKER_ESTIMATED_HEIGHT = 380;
const REACTION_PICKER_ESTIMATED_WIDTH = 320;
const REACTION_TOOLTIP_ESTIMATED_WIDTH = 280;
const HOVER_ACTIONS_ESTIMATED_HEIGHT = 52;
const COMPOSER_MAX_HEIGHT = 180;
const REACTION_LABELS: Record<string, string> = {
  '👍': 'Like',
  '❤️': 'Love',
  '😮': 'Surprised',
  '🤪': 'Funny',
  '😂': 'Laugh',
  '😆': 'Laugh',
  '😍': 'Love',
  '👏': 'Celebrate',
  '🔥': 'Fire',
  '🎉': 'Celebrate',
  '😢': 'Sad',
  '😡': 'Angry',
};

@Component({
  selector: 'app-chat-conversation',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, UserHoverCardComponent],
  templateUrl: './chat-conversation.component.html',
  styleUrl: './chat-conversation.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChatConversationComponent {
  readonly activeChannelId = input<string | null>(null);
  readonly canAddParticipants = input(false);
  readonly canLeaveChannel = input(false);
  readonly contactAvatarUrl = input<string | null>(null);
  readonly contactName = input.required<string>();
  readonly contactPresenceStatus = input<UserPresenceDisplayStatus | null>(null);
  readonly activeGroupCallStatus = input<ChannelMeetingStatus | null>(null);
  readonly hasMoreMessages = input(false);
  readonly isStartingCall = input(false);
  readonly isUploadingAttachment = input(false);
  readonly currentUserId = input<string | null>(null);
  readonly mentionableTasks = input<ChatTaskReference[]>([]);
  readonly mentionableUsers = input<ChatMention[]>([]);
  readonly reactionUserNames = input<Record<string, string>>({});
  readonly userProfilesById = input<Record<string, { name: string; email: string | null; avatarUrl: string | null }>>({});
  readonly contactUserId = input<string | null>(null);
  readonly contactEmail = input<string | null>(null);
  // Passed from Home so the unread divider can be placed after the last acknowledged message.
  readonly lastReadMessageId = input<string | null>(null);
  readonly loadingOlderMessages = input(false);
  readonly messages = input.required<MessageItem[]>();
  readonly typingParticipants = input<string[]>([]);
  // Consumed by Home to mark the latest visible message as read.
  readonly reachedConversationBottom = output<void>();
  readonly reachedConversationTop = output<void>();
  // Consumed by Home to send a websocket message for the active channel.
  readonly addParticipantRequest = output<void>();
  readonly viewParticipantsRequest = output<void>();
  readonly renameGroupRequest = output<void>();
  readonly leaveChannelRequest = output<void>();
  readonly deleteMessageRequest = output<string>();
  readonly editMessageRequest = output<ChatEditPayload>();
  readonly endCallRequest = output<void>();
  readonly joinCallRequest = output<string>();
  readonly reactionToggleRequest = output<ChatReactionPayload>();
  readonly sendMessage = output<ChatComposerMessage>();
  readonly startCallRequest = output<MeetingCallMode>();
  readonly taskReferenceOpenRequest = output<ChatTaskReference>();
  readonly typingStateChange = output<boolean>();
  private readonly messageList = viewChild.required<ElementRef<HTMLElement>>('messageList');
  private readonly composerTextarea = viewChild.required<ElementRef<HTMLTextAreaElement>>('messageBox');
  private initializedScrollChannelId: string | null = null;
  private lastRenderedFirstMessageKey: string | null = null;
  private lastRenderedChannelId: string | null = null;
  private lastRenderedMessageKey: string | null = null;
  private pendingPrependRestore: { channelId: string | null; scrollHeight: number; scrollTop: number } | null = null;
  private pendingReferencedMessageId: string | null = null;
  private highlightedMessageId: string | null = null;
  private highlightResetTimer: ReturnType<typeof setTimeout> | null = null;
  private hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private wasNearBottom = false;
  private wasNearTop = false;
  private typingStopTimer: ReturnType<typeof setTimeout> | null = null;
  private localTypingActive = false;
  protected readonly isEmojiPickerOpen = signal(false);
  protected readonly hoveredMessageId = signal<string | null>(null);
  protected readonly editingMessageId = signal<string | null>(null);
  protected readonly editingMessagePreview = signal<string>('');
  protected readonly replyTarget = signal<ChatReplyPreview | null>(null);
  protected readonly reactionPickerMessageId = signal<string | null>(null);
  protected readonly reactionPickerPlacement = signal<'top' | 'bottom'>('bottom');
  protected readonly reactionPickerHorizontalPlacement = signal<'left' | 'right'>('right');
  protected readonly activeReactionTooltipKey = signal<string | null>(null);
  protected readonly reactionTooltipPlacement = signal<'top' | 'bottom'>('top');
  protected readonly reactionTooltipHorizontalPlacement = signal<'left' | 'right'>('left');
  protected readonly hoverActionsPlacement = signal<'top' | 'bottom'>('top');
  protected readonly imageViewerMessage = signal<MessageItem | null>(null);
  protected readonly activeMentionIndex = signal(0);
  protected readonly mentionSearchActive = signal(false);
  protected readonly mentionQuery = signal('');
  protected readonly activeTaskReferenceIndex = signal(0);
  protected readonly taskReferenceSearchActive = signal(false);
  protected readonly taskReferenceQuery = signal('');
  protected readonly pendingAttachments = signal<PendingComposerAttachment[]>([]);
  protected readonly isComposerDragActive = signal(false);
  protected readonly uploadingAttachmentLabel = signal('');
  protected readonly groupedAttachmentState = computed<GroupedAttachmentState>(() => {
    const groupedByBatchId = new Map<string, MessageItem[]>();
    const byLeadId = new Map<string, MessageItem[]>();
    const followerIds = new Set<string>();

    for (const message of this.messages()) {
      const batchId = message.attributes?.['attachment_batch_id']?.trim();
      const messageId = message.id?.trim();
      if (!batchId || !messageId || !this.hasAttachment(message) || message.deleted) {
        continue;
      }

      const existing = groupedByBatchId.get(batchId);
      if (existing) {
        existing.push(message);
      } else {
        groupedByBatchId.set(batchId, [message]);
      }
    }

    groupedByBatchId.forEach((messages) => {
      if (messages.length <= 1) {
        return;
      }

      const orderedMessages = [...messages].sort((left, right) => {
        const leftHasCaption = left.text?.trim() ? 1 : 0;
        const rightHasCaption = right.text?.trim() ? 1 : 0;
        if (leftHasCaption !== rightHasCaption) {
          return rightHasCaption - leftHasCaption;
        }

        const leftHasReply = left.attributes?.['reply_to_message_id']?.trim() ? 1 : 0;
        const rightHasReply = right.attributes?.['reply_to_message_id']?.trim() ? 1 : 0;
        if (leftHasReply !== rightHasReply) {
          return rightHasReply - leftHasReply;
        }

        return (left.createdAt || '').localeCompare(right.createdAt || '');
      });

      const leadId = orderedMessages[0]?.id?.trim();
      if (!leadId) {
        return;
      }

      byLeadId.set(leadId, orderedMessages);
      orderedMessages.slice(1).forEach((message) => {
        if (message.id) {
          followerIds.add(message.id);
        }
      });
    });

    return { byLeadId, followerIds };
  });
  protected readonly renderedMessages = computed(() => {
    const followerIds = this.groupedAttachmentState().followerIds;

    return this.messages().filter((message) => {
      const messageId = message.id?.trim();
      return !messageId || !followerIds.has(messageId);
    });
  });

  constructor() {
    effect(() => {
      if (!this.isUploadingAttachment()) {
        this.uploadingAttachmentLabel.set('');
      }
    });

    // Keeps scroll behavior aligned with channel changes, unread anchors, and newly-added messages.
    effect(() => {
      const channelId = this.activeChannelId();
      const messages = this.messages();
      const firstMessage = messages.length ? messages[0] : null;
      const firstMessageKey = firstMessage ? this.getMessageKey(firstMessage) : null;
      const lastReadMessageId = this.lastReadMessageId();
      const loadingOlderMessages = this.loadingOlderMessages();
      const latestMessage = messages.length ? messages[messages.length - 1] : null;
      const latestMessageKey = latestMessage ? this.getMessageKey(latestMessage) : null;
      const isChannelChanged = channelId !== this.lastRenderedChannelId;
      const wasNearBottomBeforeRender = this.wasNearBottom;

      if (isChannelChanged) {
        this.wasNearBottom = false;
        this.wasNearTop = false;
        this.initializedScrollChannelId = null;
        this.pendingPrependRestore = null;
        this.pendingReferencedMessageId = null;
        this.clearHighlightedMessage();
        this.clearEditingState(false);
        this.clearReplyTarget();
        this.clearHoverState();
        this.reactionPickerMessageId.set(null);
        this.reactionPickerPlacement.set('bottom');
        this.reactionPickerHorizontalPlacement.set('right');
        this.activeReactionTooltipKey.set(null);
        this.reactionTooltipPlacement.set('top');
        this.reactionTooltipHorizontalPlacement.set('left');
        this.hoverActionsPlacement.set('top');
        this.clearComposerSearch();
        this.uploadingAttachmentLabel.set('');
        this.afterLayout(() => {
          const textarea = this.composerTextarea().nativeElement;
          textarea.value = '';
          this.resizeComposer(textarea);
          this.stopTyping();
        });
      }

      this.afterLayout(() => {
        if (!messages.length) {
          this.lastRenderedFirstMessageKey = null;
          this.lastRenderedChannelId = channelId;
          this.lastRenderedMessageKey = null;
          return;
        }

        if (this.pendingPrependRestore && this.pendingPrependRestore.channelId === channelId && firstMessageKey !== this.lastRenderedFirstMessageKey) {
          const messageListElement = this.messageList().nativeElement;
          const scrollDelta = messageListElement.scrollHeight - this.pendingPrependRestore.scrollHeight;
          messageListElement.scrollTop = this.pendingPrependRestore.scrollTop + scrollDelta;
          this.pendingPrependRestore = null;
          this.wasNearTop = false;
          this.lastRenderedFirstMessageKey = firstMessageKey;
          this.lastRenderedChannelId = channelId;
          this.lastRenderedMessageKey = latestMessageKey;
          return;
        }

        if (!loadingOlderMessages && this.pendingPrependRestore && this.pendingPrependRestore.channelId === channelId && firstMessageKey === this.lastRenderedFirstMessageKey) {
          this.pendingPrependRestore = null;
        }

        if (channelId && this.initializedScrollChannelId !== channelId) {
      const didPositionInitialScroll = this.scrollToUnreadAnchorOrBottom(lastReadMessageId);
      if (!didPositionInitialScroll) {
      this.lastRenderedFirstMessageKey = firstMessageKey;
      this.lastRenderedChannelId = channelId;
      this.lastRenderedMessageKey = latestMessageKey;
      return;
      }

      this.initializedScrollChannelId = channelId;
          this.emitBottomIfNeeded();
          this.emitTopIfNeeded();
          this.lastRenderedFirstMessageKey = firstMessageKey;
          this.lastRenderedChannelId = channelId;
          this.lastRenderedMessageKey = latestMessageKey;
          return;
        }

        const hasNewLatestMessage =
          !isChannelChanged
          && Boolean(latestMessageKey)
          && latestMessageKey !== this.lastRenderedMessageKey;

        if (hasNewLatestMessage && (latestMessage?.own || wasNearBottomBeforeRender)) {
          this.scrollToBottom();
          this.emitBottomIfNeeded();
          this.lastRenderedChannelId = channelId;
          this.lastRenderedMessageKey = latestMessageKey;
          return;
        }

        if (this.isNearBottom()) {
          this.scrollToBottom();
          this.emitBottomIfNeeded();
        }

        this.lastRenderedFirstMessageKey = firstMessageKey;
        this.lastRenderedChannelId = channelId;
        this.lastRenderedMessageKey = latestMessageKey;

        this.tryResolvePendingReferencedMessage();
      });
    });
  }

  protected getContactInitials(): string {
    return this.contactName()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected getContactPresenceLabel(): string | null {
    const status = this.contactPresenceStatus();
    return status ? getUserPresenceLabel(status) : null;
  }

  protected onSendMessage(textarea: HTMLTextAreaElement): void {
    if (this.isUploadingAttachment()) {
      return;
    }

    const value = textarea.value.trim();
    const attachments = this.pendingAttachments().map((attachment) => attachment.file);
    if (!value && attachments.length === 0) {
      return;
    }

    this.stopTyping();
    const editingMessageId = this.editingMessageId();
    if (editingMessageId) {
      if (!value) {
        return;
      }
      this.editMessageRequest.emit({
        messageId: editingMessageId,
        text: value,
      });
      this.clearEditingState(false);
    } else {
      if (attachments.length) {
        this.uploadingAttachmentLabel.set(
          attachments.length === 1
            ? (attachments[0]?.name?.trim() || 'attachment')
            : `${attachments.length} files`
        );
      }
      this.sendMessage.emit({
        text: value,
        attachments,
        replyTo: this.replyTarget(),
        mentions: this.extractMentions(value),
        taskReferences: this.extractTaskReferences(value),
      });
      this.clearReplyTarget();
      this.clearPendingAttachment();
    }
    textarea.value = '';
    this.resizeComposer(textarea);
    this.clearComposerSearch();
  }

  protected onComposerEnter(event: Event, textarea: HTMLTextAreaElement): void {
    const keyboardEvent = event as KeyboardEvent;
    if (this.hasMentionSuggestions()) {
      keyboardEvent.preventDefault();
      this.insertMentionFromSuggestions(textarea);
      return;
    }

    if (this.hasTaskSuggestions()) {
      keyboardEvent.preventDefault();
      this.insertTaskReferenceFromSuggestions(textarea);
      return;
    }

    if (keyboardEvent.shiftKey) {
      return;
    }
    keyboardEvent.preventDefault();
    this.onSendMessage(textarea);
  }

  protected onComposerKeydown(event: KeyboardEvent, textarea: HTMLTextAreaElement): void {
    if (!this.hasMentionSuggestions() && !this.hasTaskSuggestions()) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveComposerSelection(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveComposerSelection(-1);
        break;
      case 'Tab':
        event.preventDefault();
        if (this.hasMentionSuggestions()) {
          this.insertMentionFromSuggestions(textarea);
          return;
        }

        this.insertTaskReferenceFromSuggestions(textarea);
        break;
      case 'Escape':
        this.clearComposerSearch();
        break;
    }
  }

  protected onComposerInput(textarea: HTMLTextAreaElement): void {
    this.resizeComposer(textarea);

    if (!textarea.value.trim()) {
      this.stopTyping();
      this.clearComposerSearch();
      return;
    }

    if (!this.localTypingActive) {
      this.localTypingActive = true;
      this.typingStateChange.emit(true);
    }

    this.resetTypingStopTimer();
    this.updateComposerSearch(textarea);
  }

  protected onComposerBlur(): void {
    this.stopTyping();
  }

  protected onComposerPaste(event: ClipboardEvent): void {
    if (this.isUploadingAttachment() || this.editingMessageId()) {
      return;
    }

    const imageFile = this.extractPastedImageFile(event.clipboardData);
    if (imageFile.length === 0) {
      return;
    }

    event.preventDefault();
    this.addPendingAttachments(imageFile);
  }

  protected onComposerDragEnter(event: DragEvent): void {
    if (this.editingMessageId() || !this.hasDraggedFile(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    this.isComposerDragActive.set(true);
  }

  protected onComposerDragOver(event: DragEvent): void {
    if (this.editingMessageId() || !this.hasDraggedFile(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    this.isComposerDragActive.set(true);
  }

  protected onComposerDragLeave(event: DragEvent): void {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && this.composerTextarea().nativeElement.closest('.composer')?.contains(nextTarget)) {
      return;
    }

    this.isComposerDragActive.set(false);
  }

  protected onComposerDrop(event: DragEvent): void {
    if (!this.hasDraggedFile(event.dataTransfer) || this.isUploadingAttachment() || this.editingMessageId()) {
      this.isComposerDragActive.set(false);
      return;
    }

    event.preventDefault();
    this.isComposerDragActive.set(false);

    const files = this.extractDroppedFiles(event.dataTransfer);
    if (files.length === 0) {
      return;
    }

    this.addPendingAttachments(files);
  }

  protected onLeaveChannel(): void {
    this.leaveChannelRequest.emit();
  }

  protected onAddParticipant(): void {
    this.addParticipantRequest.emit();
  }

  protected onViewParticipants(): void {
    this.viewParticipantsRequest.emit();
  }

  protected onRenameGroup(): void {
    this.renameGroupRequest.emit();
  }

  protected onStartCall(mode: MeetingCallMode): void {
    this.startCallRequest.emit(mode);
  }

  protected onJoinActiveCall(callUrl: string): void {
    if (!callUrl) {
      return;
    }

    this.joinCallRequest.emit(callUrl);
  }

  protected onEndCall(): void {
    this.endCallRequest.emit();
  }

  protected canEndActiveCall(): boolean {
    const activeCall = this.getDisplayedActiveCallInfo();
    const currentUserId = this.currentUserId();
    if (!activeCall || !currentUserId) {
      return false;
    }

    return activeCall.startedByUserId === currentUserId;
  }

  protected getDisplayedActiveCallInfo(): { url: string; mode: MeetingCallMode; startedByUserId: string | null } | null {
    if (this.canAddParticipants()) {
      const groupStatus = this.activeGroupCallStatus();
      if (!groupStatus?.active) {
        return null;
      }

      return {
        url: groupStatus.room_url?.trim() || groupStatus.channel_id,
        mode: groupStatus.mode === 'audio' ? 'audio' : 'video',
        startedByUserId: groupStatus.started_by_user_id?.trim() || null,
      };
    }

    return this.getActiveCallInfo();
  }

  protected getActiveCallInfo(): { url: string; mode: MeetingCallMode; startedByUserId: string | null } | null {
    const messages = this.messages();
    const maxActiveAgeMs = 2 * 60 * 60 * 1000;
    const isGroupConversation = this.canAddParticipants();
    let lastStartIndex = -1;
    let lastEndIndex = -1;
    let lastStartUrl = '';
    let lastStartMode: MeetingCallMode = 'video';
    let lastStartAtMs = 0;
    let lastStartedByUserId: string | null = null;

    messages.forEach((message, index) => {
      if (!message.system) {
        return;
      }

      const event = message.attributes?.['event'];
      if (event === 'call_started') {
        const callUrl = message.attributes?.['call_url'];
        if (!callUrl) {
          return;
        }
        lastStartIndex = index;
        lastStartUrl = callUrl;
        lastStartMode = message.attributes?.['call_mode'] === 'audio' ? 'audio' : 'video';
        lastStartedByUserId = message.attributes?.['started_by_user_id'] ?? null;
        const parsed = Date.parse(message.createdAt || '');
        lastStartAtMs = Number.isFinite(parsed) ? parsed : 0;
      }

      if (event === 'call_ended') {
        lastEndIndex = index;
      }
    });

    if (lastStartIndex < 0) {
      return null;
    }

    if (!isGroupConversation && lastEndIndex > lastStartIndex) {
      return null;
    }

    if (lastStartAtMs && Date.now() - lastStartAtMs > maxActiveAgeMs) {
      return null;
    }

    return { url: lastStartUrl, mode: lastStartMode, startedByUserId: lastStartedByUserId };
  }

  protected canDeleteMessage(message: MessageItem): boolean {
    return Boolean(message.own && message.id && !message.system && !message.deleted);
  }

  protected canReplyMessage(message: MessageItem): boolean {
    return Boolean(!message.system && !message.deleted && message.id);
  }

  protected hasMessageActions(message: MessageItem): boolean {
    return this.canReplyMessage(message) || this.canEditMessage(message) || this.canDeleteMessage(message);
  }

  protected canReactToMessage(message: MessageItem): boolean {
    return Boolean(!message.system && !message.deleted && message.id);
  }

  protected hasHoverActions(message: MessageItem): boolean {
    return this.canReactToMessage(message) || this.hasMessageActions(message);
  }

  protected getDefaultReactionEmojis(): readonly string[] {
    return DEFAULT_REACTION_EMOJIS;
  }

  protected onAttachmentInputChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    if (this.editingMessageId()) {
      input.value = '';
      return;
    }

    const files = Array.from(input.files ?? []);
    if (files.length === 0) {
      return;
    }

    this.addPendingAttachments(files);
    input.value = '';
  }

  protected removePendingAttachment(attachmentId: string): void {
    if (this.isUploadingAttachment()) {
      return;
    }

    this.removePendingAttachmentById(attachmentId);
  }

  protected hasPendingAttachmentPreview(attachment: PendingComposerAttachment): boolean {
    return Boolean(attachment.previewUrl);
  }

  protected getPendingAttachmentName(attachment: PendingComposerAttachment): string {
    return attachment.file?.name?.trim() || 'Attachment';
  }

  protected getPendingAttachmentMeta(attachment: PendingComposerAttachment): string {
    return this.formatFileSize(attachment.file.size) || attachment.file.type || 'File attachment';
  }

  protected canAddMoreAttachments(): boolean {
    return this.pendingAttachments().length < MAX_PENDING_ATTACHMENTS;
  }

  protected getAttachmentSlotsLabel(): string {
    const remaining = Math.max(0, MAX_PENDING_ATTACHMENTS - this.pendingAttachments().length);
    return `${remaining} of ${MAX_PENDING_ATTACHMENTS} slots left`;
  }

  private extractPastedImageFile(clipboardData: DataTransfer | null): File[] {
    if (!clipboardData) {
      return [];
    }

    const items = Array.from(clipboardData.items ?? []);
    return items
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file)
      .map((file) => {
        if (file.name.trim()) {
          return file;
        }

        return new File(
          [file],
          this.buildPastedImageFileName(file.type),
          { type: file.type || 'image/png' },
        );
      });
  }

  private buildPastedImageFileName(contentType: string | null | undefined): string {
    const normalizedType = (contentType ?? '').trim().toLowerCase();
    const extension = normalizedType.startsWith('image/')
      ? normalizedType.slice('image/'.length).replace('jpeg', 'jpg')
      : 'png';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `pasted-image-${timestamp}.${extension || 'png'}`;
  }

  private addPendingAttachments(files: File[]): void {
    if (!files.length) {
      return;
    }

    const existing = this.pendingAttachments();
    const remainingSlots = Math.max(0, MAX_PENDING_ATTACHMENTS - existing.length);
    if (remainingSlots === 0) {
      return;
    }

    const additions = files
      .slice(0, remainingSlots)
      .map((file, index) => ({
        id: `${Date.now()}-${existing.length + index}-${file.name}`,
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      }));

    this.pendingAttachments.set([...existing, ...additions]);
  }

  private clearPendingAttachment(): void {
    this.pendingAttachments().forEach((attachment) => {
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    });
    this.pendingAttachments.set([]);
    this.isComposerDragActive.set(false);
  }

  private removePendingAttachmentById(attachmentId: string): void {
    const existing = this.pendingAttachments();
    const nextAttachments: PendingComposerAttachment[] = [];
    existing.forEach((attachment) => {
      if (attachment.id === attachmentId) {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
        return;
      }

      nextAttachments.push(attachment);
    });

    this.pendingAttachments.set(nextAttachments);
  }

  private extractDroppedFiles(dataTransfer: DataTransfer | null): File[] {
    if (!dataTransfer) {
      return [];
    }

    return Array.from(dataTransfer.files ?? []);
  }

  private hasDraggedFile(dataTransfer: DataTransfer | null): boolean {
    if (!dataTransfer) {
      return false;
    }

    return Array.from(dataTransfer.items ?? []).some((item) => item.kind === 'file');
  }

  private formatFileSize(size: number | null | undefined): string {
    if (!size || size <= 0) {
      return '';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let value = size;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const fractionDigits = value >= 10 || unitIndex === 0 ? 0 : 1;
    return `${value.toFixed(fractionDigits)} ${units[unitIndex]}`;
  }

  protected canEditMessage(message: MessageItem): boolean {
    return Boolean(message.own && message.id && !message.system && !message.deleted);
  }

  protected onDeleteMessage(messageId?: string): void {
    if (!messageId) {
      return;
    }

    this.deleteMessageRequest.emit(messageId);
  }

  protected onEditMessage(message: MessageItem): void {
    if (!message.id || !this.canEditMessage(message)) {
      return;
    }

    this.clearReplyTarget();
    this.editingMessageId.set(message.id);
    this.editingMessagePreview.set(message.text);

    const textarea = this.composerTextarea().nativeElement;
    textarea.value = message.text;
    this.resizeComposer(textarea);
    textarea.focus();
    const cursorPosition = textarea.value.length;
    textarea.setSelectionRange(cursorPosition, cursorPosition);
    this.onComposerInput(textarea);
  }

  protected cancelEditing(): void {
    this.clearEditingState(true);
  }

  protected onReplyToMessage(message: MessageItem): void {
    if (!this.canReplyMessage(message) || !message.id) {
      return;
    }

    this.clearEditingState(true);
    this.replyTarget.set(this.buildReplyPreview(message));
    this.composerTextarea().nativeElement.focus();
  }

  protected cancelReply(): void {
    this.clearReplyTarget();
  }

  protected getFilteredMentionableUsers(): ChatMention[] {
    const query = this.mentionQuery().trim().toLowerCase();
    const mentionableUsers = this.mentionableUsers();

    if (!query) {
      return mentionableUsers.slice(0, 6);
    }

    return mentionableUsers
      .filter((candidate) => candidate.name.toLowerCase().includes(query))
      .slice(0, 6);
  }

  protected hasMentionSuggestions(): boolean {
    return this.mentionSearchActive() && this.getFilteredMentionableUsers().length > 0;
  }

  protected isMentionSuggestionActive(index: number): boolean {
    return this.activeMentionIndex() === index;
  }

  protected onMentionSuggestionSelected(user: ChatMention): void {
    const textarea = this.composerTextarea().nativeElement;
    this.insertMention(textarea, user);
  }

  protected getFilteredTaskReferences(): ChatTaskReference[] {
    const query = this.taskReferenceQuery().trim().toLowerCase();
    const taskReferences = this.mentionableTasks();

    if (!query) {
      return taskReferences.slice(0, 6);
    }

    return taskReferences
      .filter((candidate) => {
        const title = candidate.title.toLowerCase();
        const taskCode = candidate.taskCode?.toLowerCase() ?? '';
        return title.includes(query) || taskCode.includes(query);
      })
      .slice(0, 6);
  }

  protected hasTaskSuggestions(): boolean {
    return this.taskReferenceSearchActive() && this.getFilteredTaskReferences().length > 0;
  }

  protected isTaskSuggestionActive(index: number): boolean {
    return this.activeTaskReferenceIndex() === index;
  }

  protected onTaskSuggestionSelected(taskReference: ChatTaskReference): void {
    const textarea = this.composerTextarea().nativeElement;
    this.insertTaskReference(textarea, taskReference);
  }

  protected getMessageTextSegments(message: MessageItem): MessageTextSegment[] {
    const text = message.text ?? '';
    const mentions = this.getMessageMentions(message);
    const taskReferences = this.getMessageTaskReferences(message);
    if (!text || (!mentions.length && !taskReferences.length)) {
      return [{ text, mention: false, taskReference: false, mentionedCurrentUser: false }];
    }

    const ranges: Array<
      | { start: number; end: number; kind: 'mention'; mention: ChatMention }
      | { start: number; end: number; kind: 'task'; taskReference: ChatTaskReference }
    > = [];

    for (const mention of mentions) {
      const token = `@${mention.name}`;
      let startIndex = text.indexOf(token);

      while (startIndex >= 0) {
        ranges.push({
          start: startIndex,
          end: startIndex + token.length,
          kind: 'mention',
          mention,
        });
        startIndex = text.indexOf(token, startIndex + token.length);
      }
    }

    ranges.push(...this.buildTaskReferenceRanges(text, taskReferences).map((range) => ({
      start: range.start,
      end: range.end,
      kind: 'task' as const,
      taskReference: range.reference,
    })));

    if (!ranges.length) {
      return [{ text, mention: false, taskReference: false, mentionedCurrentUser: false }];
    }

    ranges.sort((left, right) => left.start - right.start || right.end - left.end);
    const segments: MessageTextSegment[] = [];
    let cursor = 0;
    const currentUserId = this.currentUserId();

    for (const range of ranges) {
      if (range.start < cursor) {
        continue;
      }

      if (range.start > cursor) {
        segments.push({
          text: text.slice(cursor, range.start),
          mention: false,
          taskReference: false,
          mentionedCurrentUser: false,
        });
      }

      if (range.kind === 'mention') {
        segments.push({
          text: text.slice(range.start, range.end),
          mention: true,
          taskReference: false,
          mentionedCurrentUser: Boolean(currentUserId && range.mention.userId === currentUserId),
          mentionedUserId: range.mention.userId,
          mentionedUserName: range.mention.name,
        });
      } else {
        segments.push({
          text: text.slice(range.start, range.end),
          mention: false,
          taskReference: true,
          mentionedCurrentUser: false,
          taskReferenceData: range.taskReference,
        });
      }
      cursor = range.end;
    }

    if (cursor < text.length) {
      segments.push({
        text: text.slice(cursor),
        mention: false,
        taskReference: false,
        mentionedCurrentUser: false,
      });
    }

    return segments;
  }

  protected getUserProfileName(userId: string | null | undefined, fallbackName?: string | null): string {
    if (!userId) {
      return fallbackName?.trim() || 'User';
    }

    return this.userProfilesById()[userId]?.name?.trim() || fallbackName?.trim() || 'User';
  }

  protected getUserProfileEmail(userId: string | null | undefined): string | null {
    if (!userId) {
      return null;
    }

    return this.userProfilesById()[userId]?.email?.trim() || null;
  }

  protected getUserProfileAvatarUrl(userId: string | null | undefined): string | null {
    if (!userId) {
      return null;
    }

    return this.userProfilesById()[userId]?.avatarUrl ?? null;
  }

  protected getUserProfileInitials(userId: string | null | undefined, fallbackName?: string | null): string {
    return this.getInitials(this.getUserProfileName(userId, fallbackName));
  }

  protected getMessageReactions(message: MessageItem): MessageReaction[] {
    const raw = message.attributes?.['reactions_v1']?.trim();
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as Record<string, string[]>;
      const currentUserId = this.currentUserId();

      return Object.entries(parsed)
        .filter(([emoji, userIds]) => emoji && Array.isArray(userIds) && userIds.length > 0)
        .map(([emoji, userIds]) => ({
          emoji,
          count: userIds.length,
          reactedByCurrentUser: !!currentUserId && userIds.includes(currentUserId),
          userIds,
        }))
        .sort((left, right) => right.count - left.count || left.emoji.localeCompare(right.emoji));
    } catch {
      return [];
    }
  }

  protected hasPersistedReactions(message: MessageItem): boolean {
    return this.getMessageReactions(message).length > 0;
  }

  protected getReactionLabel(emoji: string): string {
    return REACTION_LABELS[emoji] || 'Reaction';
  }

  protected getReactionParticipants(reaction: MessageReaction): ReactionParticipant[] {
    const namesById = this.reactionUserNames();

    return reaction.userIds.map((userId) => {
      const resolvedName = namesById[userId]?.trim() || 'User';
      return {
        id: userId,
        name: resolvedName,
        initials: this.getInitials(resolvedName),
        accent: getAvatarAccent(userId || resolvedName),
      };
    });
  }

  protected onReactionTooltipEnter(message: MessageItem, reaction: MessageReaction, anchor: HTMLElement | null): void {
    const tooltipKey = this.getReactionTooltipKey(message, reaction);
    if (!tooltipKey) {
      return;
    }

    this.reactionTooltipPlacement.set(this.getReactionTooltipPlacement(anchor, reaction));
    this.reactionTooltipHorizontalPlacement.set(this.getReactionTooltipHorizontalPlacement(anchor));
    this.activeReactionTooltipKey.set(tooltipKey);
  }

  protected onReactionTooltipLeave(message: MessageItem, reaction: MessageReaction): void {
    const tooltipKey = this.getReactionTooltipKey(message, reaction);
    if (!tooltipKey || this.activeReactionTooltipKey() !== tooltipKey) {
      return;
    }

    this.activeReactionTooltipKey.set(null);
  }

  protected isReactionTooltipOpen(message: MessageItem, reaction: MessageReaction): boolean {
    const tooltipKey = this.getReactionTooltipKey(message, reaction);
    return Boolean(tooltipKey && this.activeReactionTooltipKey() === tooltipKey);
  }

  protected isReactionTooltipBottom(message: MessageItem, reaction: MessageReaction): boolean {
    return this.isReactionTooltipOpen(message, reaction) && this.reactionTooltipPlacement() === 'bottom';
  }

  protected isReactionTooltipRight(message: MessageItem, reaction: MessageReaction): boolean {
    return this.isReactionTooltipOpen(message, reaction) && this.reactionTooltipHorizontalPlacement() === 'right';
  }

  protected onToggleReaction(message: MessageItem, emoji: string): void {
    if (!this.canReactToMessage(message) || !message.id || !emoji.trim()) {
      return;
    }

    this.reactionToggleRequest.emit({
      messageId: message.id,
      emoji: emoji.trim(),
    });
    this.reactionPickerMessageId.set(null);
  }

  protected toggleReactionPicker(message: MessageItem, event: MouseEvent): void {
    event.stopPropagation();
    const messageId = message.id ?? null;
    if (!messageId || !this.canReactToMessage(message)) {
      return;
    }

    const isClosingCurrent = this.reactionPickerMessageId() === messageId;
    if (isClosingCurrent) {
      this.reactionPickerMessageId.set(null);
      this.reactionPickerPlacement.set('bottom');
      this.reactionPickerHorizontalPlacement.set('right');
      return;
    }

    const anchor = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.reactionPickerPlacement.set(this.getReactionPickerPlacement(anchor));
    this.reactionPickerHorizontalPlacement.set(this.getReactionPickerHorizontalPlacement(anchor));
    this.reactionPickerMessageId.set(messageId);
  }

  protected isReactionPickerOpenForMessage(message: MessageItem): boolean {
    return Boolean(message.id && this.reactionPickerMessageId() === message.id);
  }

  protected isReactionPickerTop(message: MessageItem): boolean {
    return this.isReactionPickerOpenForMessage(message) && this.reactionPickerPlacement() === 'top';
  }

  protected isReactionPickerLeft(message: MessageItem): boolean {
    return this.isReactionPickerOpenForMessage(message) && this.reactionPickerHorizontalPlacement() === 'left';
  }

  protected onMessageBubbleEnter(message: MessageItem, anchor: HTMLElement | null): void {
    if (!message.id) {
      return;
    }

    this.clearHoverCloseTimer();
    this.hoverActionsPlacement.set(this.getHoverActionsPlacement(anchor));
    this.hoveredMessageId.set(message.id);
  }

  protected onMessageBubbleLeave(message: MessageItem): void {
    if (!message.id || this.hoveredMessageId() !== message.id) {
      return;
    }

    this.queueHoverClose(message.id);
  }

  protected onHoverActionsEnter(message: MessageItem): void {
    if (!message.id) {
      return;
    }

    this.clearHoverCloseTimer();
    this.hoveredMessageId.set(message.id);
  }

  protected onHoverActionsLeave(message: MessageItem): void {
    if (!message.id || this.hoveredMessageId() !== message.id) {
      return;
    }

    this.queueHoverClose(message.id);
  }

  protected isHoverActionsVisible(message: MessageItem): boolean {
    return Boolean(message.id && this.hoveredMessageId() === message.id);
  }

  protected isHoverActionsBottom(message: MessageItem): boolean {
    return this.isHoverActionsVisible(message) && this.hoverActionsPlacement() === 'bottom';
  }

  protected hasChannelMenuActions(): boolean {
    return this.canAddParticipants() || this.canLeaveChannel();
  }

  protected toggleEmojiPicker(event: MouseEvent): void {
    event.stopPropagation();
    this.isEmojiPickerOpen.update((isOpen) => !isOpen);
  }

  protected onEmojiPickerClick(event: Event): void {
    event.stopPropagation();
  }

  protected onReactionPickerClick(event: Event): void {
    event.stopPropagation();
  }

  protected onEmojiPicked(event: Event): void {
    const emoji = (event as CustomEvent<{ unicode?: string }>).detail?.unicode;
    if (!emoji) {
      return;
    }

    const textarea = this.composerTextarea().nativeElement;
    const selectionStart = textarea.selectionStart ?? textarea.value.length;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;

    textarea.setRangeText(emoji, selectionStart, selectionEnd, 'end');
    textarea.focus();
    this.onComposerInput(textarea);
    this.isEmojiPickerOpen.set(false);
  }

  protected onReactionEmojiPicked(message: MessageItem, event: Event): void {
    const emoji = (event as CustomEvent<{ unicode?: string }>).detail?.unicode;
    if (!emoji) {
      return;
    }

    this.onToggleReaction(message, emoji);
  }

  protected getTypingLabel(): string {
    const participants = this.typingParticipants().map((name) => this.formatParticipantName(name));
    if (!participants.length) {
      return '';
    }

    if (participants.length === 1) {
      return `${participants[0]} is typing...`;
    }

    if (participants.length === 2) {
      return `${participants[0]} and ${participants[1]} are typing...`;
    }

    return `${participants[0]} and ${participants.length - 1} others are typing...`;
  }

  protected isEditingMessage(message: MessageItem): boolean {
    return Boolean(message.id && this.editingMessageId() === message.id);
  }

  private queueHoverClose(messageId: string): void {
    this.clearHoverCloseTimer();
    this.hoverCloseTimer = setTimeout(() => {
      if (this.hoveredMessageId() === messageId) {
        this.hoveredMessageId.set(null);
      }
      this.hoverCloseTimer = null;
    }, 140);
  }

  private clearHoverCloseTimer(): void {
    if (!this.hoverCloseTimer) {
      return;
    }

    clearTimeout(this.hoverCloseTimer);
    this.hoverCloseTimer = null;
  }

  private clearHoverState(): void {
    this.clearHoverCloseTimer();
    this.hoveredMessageId.set(null);
  }

  private extractMentions(text: string): ChatMention[] {
    const normalizedText = text.trim();
    if (!normalizedText) {
      return [];
    }

    const seen = new Set<string>();
    const mentions: ChatMention[] = [];

    for (const candidate of this.mentionableUsers()) {
      const userId = candidate.userId?.trim();
      const name = candidate.name?.trim();
      if (!userId || !name || seen.has(userId)) {
        continue;
      }

      if (normalizedText.includes(`@${name}`)) {
        mentions.push({ userId, name });
        seen.add(userId);
      }
    }

    return mentions;
  }

  private extractTaskReferences(text: string): ChatTaskReference[] {
    const normalizedText = text.trim();
    if (!normalizedText) {
      return [];
    }

    const seen = new Set<string>();

    return this.buildTaskReferenceRanges(normalizedText, this.mentionableTasks())
      .map((range) => range.reference)
      .filter((reference) => {
        const taskId = reference.taskId?.trim();
        if (!taskId || seen.has(taskId)) {
          return false;
        }

        seen.add(taskId);
        return true;
      });
  }

  private getMessageMentions(message: MessageItem): ChatMention[] {
    const raw = message.attributes?.['mentioned_users_v1']?.trim();
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as Array<{ user_id?: string; name?: string }>;
      return parsed
        .map((entry) => ({
          userId: entry.user_id?.trim() ?? '',
          name: entry.name?.trim() ?? '',
        }))
        .filter((entry) => entry.userId && entry.name);
    } catch {
      return [];
    }
  }

  private getMessageTaskReferences(message: MessageItem): ChatTaskReference[] {
    const raw = message.attributes?.['task_references_v1']?.trim();
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as Array<{
        task_id?: string;
        project_id?: string;
        title?: string;
        task_code?: string;
        label?: string;
      }>;

      return parsed
        .map((entry) => ({
          taskId: entry.task_id?.trim() ?? '',
          projectId: entry.project_id?.trim() ?? '',
          title: entry.title?.trim() ?? '',
          taskCode: entry.task_code?.trim() || null,
          label: entry.label?.trim() || entry.title?.trim() || '',
        }))
        .filter((entry) => entry.taskId && entry.projectId && entry.label);
    } catch {
      return [];
    }
  }

  private updateComposerSearch(textarea: HTMLTextAreaElement): void {
    const match = this.findActiveMention(textarea);
    if (match) {
      this.mentionSearchActive.set(true);
      this.mentionQuery.set(match.query);
      this.activeMentionIndex.set(0);
      this.clearTaskSearch();
      return;
    }

    const taskMatch = this.findActiveTaskReference(textarea);
    if (taskMatch) {
      this.taskReferenceSearchActive.set(true);
      this.taskReferenceQuery.set(taskMatch.query);
      this.activeTaskReferenceIndex.set(0);
      this.clearMentionSearch();
      return;
    }

    this.clearComposerSearch();
  }

  private clearMentionSearch(): void {
    this.mentionSearchActive.set(false);
    this.mentionQuery.set('');
    this.activeMentionIndex.set(0);
  }

  private clearTaskSearch(): void {
    this.taskReferenceSearchActive.set(false);
    this.taskReferenceQuery.set('');
    this.activeTaskReferenceIndex.set(0);
  }

  private clearComposerSearch(): void {
    this.clearMentionSearch();
    this.clearTaskSearch();
  }

  private findActiveMention(textarea: HTMLTextAreaElement): ComposerTokenMatch | null {
    const value = textarea.value ?? '';
    const caretPosition = textarea.selectionStart ?? value.length;
    const beforeCaret = value.slice(0, caretPosition);
    const match = beforeCaret.match(/(^|\s)@([a-zA-Z0-9._-]*)$/);
    if (!match) {
      return null;
    }

    const query = match[2] ?? '';
    const atIndex = beforeCaret.lastIndexOf(`@${query}`);
    if (atIndex < 0) {
      return null;
    }

    return {
      query,
      start: atIndex,
      end: caretPosition,
    };
  }

  private findActiveTaskReference(textarea: HTMLTextAreaElement): ComposerTokenMatch | null {
    const value = textarea.value ?? '';
    const caretPosition = textarea.selectionStart ?? value.length;
    const beforeCaret = value.slice(0, caretPosition);
    const match = beforeCaret.match(/(^|\s)#([^\n]*)$/);
    if (!match) {
      return null;
    }

    const query = match[2] ?? '';
    if (query.endsWith(' ')) {
      return null;
    }

    const hashIndex = beforeCaret.lastIndexOf(`#${query}`);
    if (hashIndex < 0) {
      return null;
    }

    return {
      query,
      start: hashIndex,
      end: caretPosition,
    };
  }

  private moveComposerSelection(direction: 1 | -1): void {
    if (this.hasMentionSuggestions()) {
      this.moveMentionSelection(direction);
      return;
    }

    this.moveTaskReferenceSelection(direction);
  }

  private moveMentionSelection(direction: 1 | -1): void {
    const suggestions = this.getFilteredMentionableUsers();
    if (!suggestions.length) {
      this.activeMentionIndex.set(0);
      return;
    }

    const nextIndex = (this.activeMentionIndex() + direction + suggestions.length) % suggestions.length;
    this.activeMentionIndex.set(nextIndex);
  }

  private moveTaskReferenceSelection(direction: 1 | -1): void {
    const suggestions = this.getFilteredTaskReferences();
    if (!suggestions.length) {
      this.activeTaskReferenceIndex.set(0);
      return;
    }

    const nextIndex = (this.activeTaskReferenceIndex() + direction + suggestions.length) % suggestions.length;
    this.activeTaskReferenceIndex.set(nextIndex);
  }

  private insertMentionFromSuggestions(textarea: HTMLTextAreaElement): void {
    const suggestions = this.getFilteredMentionableUsers();
    if (!suggestions.length) {
      return;
    }

    const selectedUser = suggestions[Math.min(this.activeMentionIndex(), suggestions.length - 1)];
    this.insertMention(textarea, selectedUser);
  }

  private insertTaskReferenceFromSuggestions(textarea: HTMLTextAreaElement): void {
    const suggestions = this.getFilteredTaskReferences();
    if (!suggestions.length) {
      return;
    }

    const selectedTaskReference = suggestions[Math.min(this.activeTaskReferenceIndex(), suggestions.length - 1)];
    this.insertTaskReference(textarea, selectedTaskReference);
  }

  private insertMention(textarea: HTMLTextAreaElement, user: ChatMention): void {
    const match = this.findActiveMention(textarea);
    if (!match) {
      return;
    }

    const mentionToken = `@${user.name} `;
    textarea.setRangeText(mentionToken, match.start, match.end, 'end');
    textarea.focus();
    this.clearComposerSearch();
    this.onComposerInput(textarea);
  }

  private insertTaskReference(textarea: HTMLTextAreaElement, taskReference: ChatTaskReference): void {
    const match = this.findActiveTaskReference(textarea);
    if (!match) {
      return;
    }

    const taskToken = `#${taskReference.label} `;
    textarea.setRangeText(taskToken, match.start, match.end, 'end');
    textarea.focus();
    this.clearComposerSearch();
    this.onComposerInput(textarea);
  }

  private buildTaskReferenceRanges(
    text: string,
    taskReferences: ChatTaskReference[],
  ): Array<{ start: number; end: number; reference: ChatTaskReference }> {
    const ranges: Array<{ start: number; end: number; reference: ChatTaskReference }> = [];
    const occupiedRanges: Array<{ start: number; end: number }> = [];
    const sortedReferences = [...taskReferences]
      .filter((reference) => reference.taskId?.trim() && reference.projectId?.trim() && reference.label?.trim())
      .sort((left, right) => right.label.length - left.label.length);

    for (const reference of sortedReferences) {
      const token = `#${reference.label}`;
      let startIndex = text.indexOf(token);

      while (startIndex >= 0) {
        const endIndex = startIndex + token.length;
        const nextCharacter = text.charAt(endIndex);
        const overlapsExistingRange = occupiedRanges.some((range) => startIndex < range.end && endIndex > range.start);

        if (!overlapsExistingRange && this.isTaskReferenceBoundaryCharacter(nextCharacter)) {
          ranges.push({
            start: startIndex,
            end: endIndex,
            reference,
          });
          occupiedRanges.push({ start: startIndex, end: endIndex });
        }

        startIndex = text.indexOf(token, startIndex + token.length);
      }
    }

    return ranges.sort((left, right) => left.start - right.start || right.end - left.end);
  }

  private isTaskReferenceBoundaryCharacter(character: string): boolean {
    return !character || /\s|[.,!?;:)\]}]/.test(character);
  }

  protected openTaskReference(reference: ChatTaskReference | null | undefined): void {
    if (!reference?.taskId?.trim() || !reference.projectId?.trim()) {
      return;
    }

    this.taskReferenceOpenRequest.emit(reference);
  }

  private resizeComposer(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, COMPOSER_MAX_HEIGHT);
    textarea.style.height = `${Math.max(nextHeight, 36)}px`;
    textarea.style.overflowY = textarea.scrollHeight > COMPOSER_MAX_HEIGHT ? 'auto' : 'hidden';
  }

  private getInitials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected getReplyPreview(message: MessageItem): ChatReplyPreview | null {
    const replyMessageId = message.attributes?.['reply_to_message_id']?.trim();
    const replySender = message.attributes?.['reply_to_sender']?.trim();
    const replyText = message.attributes?.['reply_to_text']?.trim();
    const replyAttachmentName = message.attributes?.['reply_to_attachment_name']?.trim();

    if (!replyMessageId || !replySender || (!replyText && !replyAttachmentName)) {
      return null;
    }

    return {
      messageId: replyMessageId,
      sender: replySender,
      text: replyText || 'Attachment',
      attachmentName: replyAttachmentName || null,
    };
  }

  protected hasAttachment(message: MessageItem): boolean {
    return Boolean(message.attachmentUrl && message.attachmentName);
  }

  protected shouldRenderMessage(message: MessageItem): boolean {
    const messageId = message.id?.trim();
    if (!messageId) {
      return true;
    }

    return !this.groupedAttachmentState().followerIds.has(messageId);
  }

  protected getRenderedAttachmentMessages(message: MessageItem): MessageItem[] {
    const messageId = message.id?.trim();
    if (!messageId) {
      return [message];
    }

    return this.groupedAttachmentState().byLeadId.get(messageId) ?? [message];
  }

  protected isImageAttachment(message: MessageItem): boolean {
    return Boolean(this.hasAttachment(message) && message.attachmentContentType?.startsWith('image/'));
  }

  protected isPdfAttachment(message: MessageItem): boolean {
    return Boolean(this.hasAttachment(message) && message.attachmentContentType === 'application/pdf');
  }

  protected formatAttachmentSize(message: MessageItem): string {
    const size = message.attachmentSize;
    if (!size || size <= 0) {
      return '';
    }

    if (size >= 1024 * 1024) {
      return `${(size / 1024 / 1024).toFixed(2)} MB`;
    }

    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  protected openAttachment(message: MessageItem): void {
    if (!message.attachmentUrl || typeof window === 'undefined') {
      return;
    }

    window.open(message.attachmentUrl, '_blank', 'noopener,noreferrer');
  }

  protected openImageViewer(message: MessageItem): void {
    if (!this.isImageAttachment(message) || !message.attachmentUrl) {
      return;
    }

    this.imageViewerMessage.set(message);
  }

  protected closeImageViewer(): void {
    this.imageViewerMessage.set(null);
  }

  protected async downloadAttachment(message: MessageItem): Promise<void> {
    const downloadUrl = message.attachmentDownloadUrl || message.attachmentUrl;
    if (!downloadUrl || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    try {
      const response = await fetch(downloadUrl, { credentials: 'omit' });
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = message.attachmentName || 'attachment';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = message.attachmentName || 'attachment';
      anchor.rel = 'noreferrer';
      anchor.target = '_blank';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    }
  }

  protected onAttachmentImageLoad(message: MessageItem): void {
    const messages = this.messages();
    const latestMessage = messages.length ? messages[messages.length - 1] : null;
    const isLatestMessage = Boolean(
      latestMessage
      && latestMessage.channelId === message.channelId
      && this.getMessageKey(latestMessage) === this.getMessageKey(message)
    );

    if (!isLatestMessage && !message.own) {
      return;
    }

    if (message.own || this.wasNearBottom || this.isNearBottom()) {
      this.afterLayout(() => {
        this.scrollToBottom();
        this.emitBottomIfNeeded();
      });
    }
  }

  protected shouldShowDaySeparator(index: number, dayLabel?: string): boolean {
    if (!dayLabel) {
      return false;
    }

    if (index === 0) {
      return true;
    }

    const previousMessage = this.renderedMessages()[index - 1];
    return previousMessage?.dayLabel !== dayLabel;
  }

  protected shouldShowUnreadSeparator(index: number, messageId?: string, own?: boolean): boolean {
    // When user is already at the bottom of the open chat, treat it as actively read.
    if (this.wasNearBottom) {
      return false;
    }

    const firstUnreadRenderedMessageId = this.getFirstUnreadRenderedMessageId(this.lastReadMessageId());
    if (!firstUnreadRenderedMessageId || !messageId || own) {
      return false;
    }

    return messageId === firstUnreadRenderedMessageId;
  }

  protected onMessageListScroll(): void {
    this.emitBottomIfNeeded();
    this.emitTopIfNeeded();
  }

  protected scrollToReferencedMessage(messageId: string): void {
    if (!messageId) {
      return;
    }

    if (this.scrollToMessageIfRendered(messageId)) {
      return;
    }

    this.pendingReferencedMessageId = messageId;
    this.requestOlderMessagesForReferencedJump();
  }

  @HostListener('document:click')
  protected closeFloatingPickers(): void {
    if (this.isEmojiPickerOpen()) {
      this.isEmojiPickerOpen.set(false);
    }

    if (this.reactionPickerMessageId()) {
      this.reactionPickerMessageId.set(null);
      this.reactionPickerPlacement.set('bottom');
    }

    if (this.hasMentionSuggestions() || this.hasTaskSuggestions()) {
      this.clearComposerSearch();
    }
  }

  @HostListener('document:keydown.escape')
  protected handleEscapeKey(): void {
    if (this.imageViewerMessage()) {
      this.closeImageViewer();
    }

    if (this.hasMentionSuggestions() || this.hasTaskSuggestions()) {
      this.clearComposerSearch();
    }
  }

  // Used on first render for a channel so unread chats open at the first unread message position.
  private scrollToUnreadAnchorOrBottom(lastReadMessageId: string | null): boolean {
    if (!lastReadMessageId) {
      this.scrollToBottom();
      return true;
    }

    const messageListElement = this.messageList().nativeElement;
    const firstUnreadMessageId = this.getFirstUnreadRenderedMessageId(lastReadMessageId);

    if (firstUnreadMessageId) {
      const unreadMessageElement = messageListElement.querySelector<HTMLElement>(
        `[data-message-id="${CSS.escape(firstUnreadMessageId)}"]`
      );

      if (unreadMessageElement) {
          this.scrollElementToViewportCenter(unreadMessageElement);
        return true;
      }

      return false;
    }

    const unreadAnchor = messageListElement.querySelector<HTMLElement>('[data-unread-anchor="true"]');

    if (unreadAnchor) {
      this.scrollElementToViewportCenter(unreadAnchor);
      return true;
    }

    const rawMessages = this.messages();
    const lastReadIndex = rawMessages.findIndex((message) => message.id === lastReadMessageId);
    if (lastReadIndex >= rawMessages.length - 1) {
      this.scrollToBottom();
      return true;
    }

    return false;
  }

  private getFirstUnreadRenderedMessageId(lastReadMessageId: string | null): string | null {
    if (!lastReadMessageId) {
      return null;
    }

    const rawMessages = this.messages();
    const lastReadIndex = rawMessages.findIndex((message) => message.id === lastReadMessageId);
    if (lastReadIndex === -1) {
      return null;
    }

    for (let index = lastReadIndex + 1; index < rawMessages.length; index += 1) {
      const candidate = rawMessages[index];
      const candidateId = candidate?.id?.trim();
      if (!candidateId || candidate.own) {
        continue;
      }

      if (this.groupedAttachmentState().followerIds.has(candidateId)) {
        continue;
      }

      return candidateId;
    }

    return null;
  }

  // Bottom detection drives both auto-scroll behavior and mark-read events back to Home.
  private isNearBottom(): boolean {
    const messageListElement = this.messageList().nativeElement;
    const distanceToBottom =
      messageListElement.scrollHeight
      - messageListElement.scrollTop
      - messageListElement.clientHeight;

    return distanceToBottom <= 12;
  }

  private isNearTop(): boolean {
    const messageListElement = this.messageList().nativeElement;
    return messageListElement.scrollTop <= 24;
  }

  // Emits only on edge transition so Home does not spam mark-read requests while the user stays at bottom.
  private emitBottomIfNeeded(): void {
    const isNearBottom = this.isNearBottom();
    const isScrollable = this.isScrollable();

    if (isNearBottom && (!this.wasNearBottom || !isScrollable)) {
      this.reachedConversationBottom.emit();
    }

    this.wasNearBottom = isNearBottom;
  }

  private emitTopIfNeeded(): void {
    const isNearTop = this.isNearTop();

    if (isNearTop && !this.wasNearTop && this.hasMoreMessages() && !this.loadingOlderMessages() && !this.pendingPrependRestore) {
      const messageListElement = this.messageList().nativeElement;
      this.pendingPrependRestore = {
        channelId: this.activeChannelId(),
        scrollHeight: messageListElement.scrollHeight,
        scrollTop: messageListElement.scrollTop,
      };
      this.reachedConversationTop.emit();
    }

    this.wasNearTop = isNearTop;
  }

  private isScrollable(): boolean {
    const messageListElement = this.messageList().nativeElement;
    return messageListElement.scrollHeight > messageListElement.clientHeight + 1;
  }

  private getMessageKey(message: MessageItem): string {
    return message.id || message.createdAt || `${message.time}-${message.text}`;
  }

  private scrollToBottom(): void {
    const messageListElement = this.messageList().nativeElement;
    messageListElement.scrollTop = messageListElement.scrollHeight;
  }

  private scrollElementToViewportCenter(element: HTMLElement): void {
    const messageListElement = this.messageList().nativeElement;
    const targetOffset = Math.max(
      element.offsetTop - (messageListElement.clientHeight / 2) + (element.clientHeight / 2),
      0
    );
    messageListElement.scrollTop = targetOffset;
  }

  private afterLayout(callback: () => void): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  }

  private getReactionPickerPlacement(anchor: HTMLElement | null): 'top' | 'bottom' {
    if (!anchor || typeof window === 'undefined') {
      return 'bottom';
    }

    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < REACTION_PICKER_ESTIMATED_HEIGHT && spaceAbove > spaceBelow) {
      return 'top';
    }

    return 'bottom';
  }

  private getReactionPickerHorizontalPlacement(anchor: HTMLElement | null): 'left' | 'right' {
    if (!anchor || typeof window === 'undefined') {
      return 'right';
    }

    const messageListElement = this.messageList().nativeElement;
    const listRect = messageListElement.getBoundingClientRect();
    const rect = anchor.getBoundingClientRect();
    const spaceForRightAlignedPicker = rect.right - listRect.left;
    const spaceForLeftAlignedPicker = listRect.right - rect.left;

    if (
      spaceForRightAlignedPicker < REACTION_PICKER_ESTIMATED_WIDTH
      && spaceForLeftAlignedPicker > spaceForRightAlignedPicker
    ) {
      return 'left';
    }

    return 'right';
  }

  private getReactionTooltipPlacement(anchor: HTMLElement | null, reaction: MessageReaction): 'top' | 'bottom' {
    if (!anchor || typeof window === 'undefined') {
      return 'top';
    }

    const estimatedHeight = 52 + Math.min(Math.max(reaction.userIds.length, 1), 6) * 30;
    const messageListElement = this.messageList().nativeElement;
    const listRect = messageListElement.getBoundingClientRect();
    const rect = anchor.getBoundingClientRect();
    const spaceAbove = rect.top - listRect.top;
    const spaceBelow = listRect.bottom - rect.bottom;

    if (spaceAbove < estimatedHeight && spaceBelow > spaceAbove) {
      return 'bottom';
    }

    return 'top';
  }

  private getReactionTooltipHorizontalPlacement(anchor: HTMLElement | null): 'left' | 'right' {
    if (!anchor || typeof window === 'undefined') {
      return 'left';
    }

    const messageListElement = this.messageList().nativeElement;
    const listRect = messageListElement.getBoundingClientRect();
    const rect = anchor.getBoundingClientRect();
    const spaceForLeftAlignedTooltip = listRect.right - rect.left;
    const spaceForRightAlignedTooltip = rect.right - listRect.left;

    if (
      spaceForLeftAlignedTooltip < REACTION_TOOLTIP_ESTIMATED_WIDTH
      && spaceForRightAlignedTooltip > spaceForLeftAlignedTooltip
    ) {
      return 'right';
    }

    return 'left';
  }

  private getReactionTooltipKey(message: MessageItem, reaction: MessageReaction): string | null {
    if (!message.id || !reaction.emoji) {
      return null;
    }

    return `${message.id}:${reaction.emoji}`;
  }

  private getHoverActionsPlacement(anchor: HTMLElement | null): 'top' | 'bottom' {
    if (!anchor || typeof window === 'undefined') {
      return 'top';
    }

    const messageListElement = this.messageList().nativeElement;
    const listRect = messageListElement.getBoundingClientRect();
    const rect = anchor.getBoundingClientRect();
    const spaceAbove = rect.top - listRect.top;
    const spaceBelow = listRect.bottom - rect.bottom;

    if (spaceAbove < HOVER_ACTIONS_ESTIMATED_HEIGHT && spaceBelow > spaceAbove) {
      return 'bottom';
    }

    return 'top';
  }

  private scrollToMessageIfRendered(messageId: string): boolean {
    const messageListElement = this.messageList().nativeElement;
    const target = messageListElement.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(messageId)}"]`);
    if (!target) {
      return false;
    }

    this.scrollElementToViewportCenter(target);
    this.highlightMessage(messageId);
    this.pendingReferencedMessageId = null;
    return true;
  }

  private tryResolvePendingReferencedMessage(): void {
    const pendingMessageId = this.pendingReferencedMessageId;
    if (!pendingMessageId) {
      return;
    }

    if (this.scrollToMessageIfRendered(pendingMessageId)) {
      return;
    }

    if (!this.loadingOlderMessages() && this.hasMoreMessages()) {
      this.requestOlderMessagesForReferencedJump();
      return;
    }

    if (!this.hasMoreMessages()) {
      this.pendingReferencedMessageId = null;
    }
  }

  private requestOlderMessagesForReferencedJump(): void {
    if (!this.pendingReferencedMessageId || this.loadingOlderMessages() || !this.hasMoreMessages() || this.pendingPrependRestore) {
      return;
    }

    const messageListElement = this.messageList().nativeElement;
    this.pendingPrependRestore = {
      channelId: this.activeChannelId(),
      scrollHeight: messageListElement.scrollHeight,
      scrollTop: messageListElement.scrollTop,
    };
    this.reachedConversationTop.emit();
  }

  private highlightMessage(messageId: string): void {
    this.highlightedMessageId = messageId;

    if (this.highlightResetTimer) {
      clearTimeout(this.highlightResetTimer);
    }

    this.highlightResetTimer = setTimeout(() => {
      if (this.highlightedMessageId === messageId) {
        this.highlightedMessageId = null;
      }
      this.highlightResetTimer = null;
    }, 1800);
  }

  protected isHighlightedMessage(message: MessageItem): boolean {
    return Boolean(message.id && this.highlightedMessageId === message.id);
  }

  private clearEditingState(clearTextarea: boolean): void {
    this.editingMessageId.set(null);
    this.editingMessagePreview.set('');
    this.clearComposerSearch();

    if (clearTextarea) {
      const textarea = this.composerTextarea().nativeElement;
      textarea.value = '';
      this.stopTyping();
    }
  }

  private clearReplyTarget(): void {
    this.replyTarget.set(null);
  }

  private clearHighlightedMessage(): void {
    this.highlightedMessageId = null;
    if (this.highlightResetTimer) {
      clearTimeout(this.highlightResetTimer);
      this.highlightResetTimer = null;
    }
  }

  private buildReplyPreview(message: MessageItem): ChatReplyPreview {
    const attachmentName = message.attachmentName ?? null;
    const replyText = this.summarizeReplyText(message);

    return {
      messageId: message.id ?? '',
      sender: message.sender,
      text: replyText,
      attachmentName,
    };
  }

  private summarizeReplyText(message: MessageItem): string {
    const raw = message.text?.trim();
    const attachmentName = message.attachmentName?.trim();
    const fallback = attachmentName ? 'Attachment' : 'Message';
    const source = raw || fallback;

    return source.length > 120 ? `${source.slice(0, 117)}...` : source;
  }

  private resetTypingStopTimer(): void {
    if (this.typingStopTimer) {
      clearTimeout(this.typingStopTimer);
    }

    this.typingStopTimer = setTimeout(() => {
      this.stopTyping();
    }, 1200);
  }

  private stopTyping(): void {
    if (this.typingStopTimer) {
      clearTimeout(this.typingStopTimer);
      this.typingStopTimer = null;
    }

    if (!this.localTypingActive) {
      return;
    }

    this.localTypingActive = false;
    this.typingStateChange.emit(false);
  }

  private formatParticipantName(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  ngOnDestroy(): void {
    this.stopTyping();
    this.clearComposerSearch();
    this.clearHighlightedMessage();
    this.clearPendingAttachment();
  }
}
